export type UploadEmployeesTranslation = {
  fileTypes: {
    CHAT_DOCUMENT: { description: string; label: string };
    COMPANY_POSITIONS: { description: string; label: string };
    DEVELOPMENT_ACTIVITIES: { description: string; label: string };
    EDUAKCJA: { description: string; label: string };
    EMPLOYEES: { description: string; label: string };
    EMPLOYEES_EMPLOYMENT_HISTORY: { description: string; label: string };
    EMPLOYEES_ENABLE_CHANGE: { description: string; label: string };
    MDR: { description: string; label: string };
    SYSTEM_USERS: { description: string; label: string };
  };
  form: {
    downloadFileTemplateLabel: string;
    dropzoneLabel: string;
    dropzoneLabel2: string;
    fileTypeDescription: string;
    resetButton: string;
    uploadButton: string;
    uploadFileLabel: string;
    uploadFileType: string;
  };
  mdrCareerPathsWarning: {
    cancelButton: string;
    confirmButton: string;
    description: string;
    manualPathsNotice: string;
    scopeNotice: string;
    title: string;
  };
  modal: {
    closeButton: string;
    downloadErrorButton: string;
    errorOccurred: string;
    errorTitle: string;
    uploadingMessage: string;
    uploadingTitle: string;
  };
  toast: {
    employeesUploadedFileError: string;
    employeesUploadedGeneralError: string;
    employeesUploadedSuccessfully: string;
  };
  validationErrors: {
    actions: {
      cancel: string;
      cannotResolveOtherType: string;
      close: string;
      closeAndReupload: string;
      continue: string;
      replaceFile: string;
      resolve: string;
      resolveAll: string;
      resolveAllOfType: string;
      resolveAllVisible: string;
      viewDetails: string;
    };
    allErrorsResolvedMessage: string;
    allErrorsResolvedTitle: string;
    businessErrorTypes: {
      BIRTHDAY_DATE_CHANGED: string;
      COMPANY_POSITION_DATA: string;
      EMPLOYEE_ENABLE_INVALID_VALUE: string;
      EMPLOYEE_POSITION_CONSISTENCY: string;
      EMPLOYEE_REQUIRED_FIELDS: string;
      FUNCTIONAL_GROUP_LEVEL_HIERARCHY_INVALID: string;
      HRBP_CHANGED: string;
      OTHER: string;
      P1_SUPERVISOR_CHANGED: string;
      P2_SUPERVISOR_CHANGED: string;
      SYSTEM_USER_INVALID_ENABLED_VALUE: string;
    };
    columns: {
      actions: string;
      column: string;
      count: string;
      errorName: string;
      errorType: string;
      level: string;
      message: string;
      row: string;
      severity: string;
    };
    criticalErrorMessage: string;
    criticalErrorsFound: string;
    criticalErrorTitle: string;
    detailsTitle: string;
    goToPage: string;
    noErrors: string;
    replaceFileDialog: {
      acceptedFormats: string;
      description: string;
      dropzoneLabel: string;
      title: string;
      uploadButton: string;
    };
    status: {
      resolved: string;
    };
    subtitle: string;
    summaryTitle: string;
    title: string;
    uploadInfo: {
      fileName: string;
      fileType: string;
      title: string;
      uploadDate: string;
    };
    validationInfoMessage: string;
    validationInfoTitle: string;
  };
};

export const uploadEmployeesTranslation: UploadEmployeesTranslation = {
  fileTypes: {
    CHAT_DOCUMENT: {
      description: 'Adds a document to the chat assistant knowledge base',
      label: 'Chat Document',
    },
    COMPANY_POSITIONS: {
      description: 'Updates company positions and organizational structure',
      label: 'Company Positions',
    },
    DEVELOPMENT_ACTIVITIES: {
      description:
        'Imports employee development activities (e.g. FRIS, BTM, Coaching) from a matrix file, matched by SAP number',
      label: 'Development Activities',
    },
    EDUAKCJA: {
      description: 'Updates employee education and training data',
      label: 'Education Data',
    },
    EMPLOYEES: {
      description: 'Updates basic employee information (name, SAP ID, position, department)',
      label: 'Employees',
    },
    EMPLOYEES_EMPLOYMENT_HISTORY: {
      description: 'Updates employee employment history and career paths',
      label: 'Employment History',
    },
    EMPLOYEES_ENABLE_CHANGE: {
      description: 'Updates employee status (active/inactive)',
      label: 'Employee Status',
    },
    MDR: {
      description: 'Updates MDR (Management Development Review) data',
      label: 'MDR Data',
    },
    SYSTEM_USERS: {
      description: 'Updates system users and access permissions',
      label: 'System Users',
    },
  },
  form: {
    downloadFileTemplateLabel: 'Download template file',
    dropzoneLabel: "Drag 'n' drop a file here, or click to select a file",
    dropzoneLabel2: '(accepted formats: .xls, .xlsx)',
    fileTypeDescription: 'What will be updated:',
    resetButton: 'Reset',
    uploadButton: 'Import',
    uploadFileLabel: 'Upload file',
    uploadFileType: 'File type to upload',
  },
  mdrCareerPathsWarning: {
    cancelButton: 'Cancel',
    confirmButton: 'Import and overwrite',
    description:
      'Career paths of the employees listed in the file will be replaced with the ones from the file. This applies both to the employee profile and to the succession module.',
    manualPathsNotice:
      'Career paths added manually in the succession module will be deleted for these employees. This cannot be undone.',
    scopeNotice: 'Employees who are not in the file remain unchanged.',
    title: 'Career paths will be overwritten',
  },
  modal: {
    closeButton: 'Close',
    downloadErrorButton: 'Download error',
    errorOccurred: 'An error occurred during upload',
    errorTitle: 'Upload Error',
    uploadingMessage: 'Uploading data, please wait...',
    uploadingTitle: 'Uploading Data',
  },
  toast: {
    employeesUploadedFileError:
      'Error occurred while uploading employees, the attached file is invalid.',
    employeesUploadedGeneralError:
      'Error occurred while uploading employees, please try again later',
    employeesUploadedSuccessfully: 'Employees uploaded successfully',
  },
  validationErrors: {
    actions: {
      cancel: 'Cancel',
      cannotResolveOtherType:
        'Cannot automatically resolve "Other" type errors - resolve them individually',
      close: 'Close',
      closeAndReupload: 'Close and re-upload',
      continue: 'Continue',
      replaceFile: 'Replace file',
      resolve: 'Resolve',
      resolveAll: 'Resolve all',
      resolveAllOfType: 'Resolve all of this type',
      resolveAllVisible: 'Resolve all visible',
      viewDetails: 'View details',
    },
    allErrorsResolvedMessage:
      'All validation errors have been resolved. You can now continue with the upload.',
    allErrorsResolvedTitle: 'All errors resolved',
    businessErrorTypes: {
      BIRTHDAY_DATE_CHANGED: 'Birth date changed',
      COMPANY_POSITION_DATA: 'Company position data',
      EMPLOYEE_ENABLE_INVALID_VALUE: 'Invalid employee status value',
      EMPLOYEE_POSITION_CONSISTENCY: 'Employee position consistency',
      EMPLOYEE_REQUIRED_FIELDS: 'Required fields missing',
      FUNCTIONAL_GROUP_LEVEL_HIERARCHY_INVALID: 'Invalid functional group level hierarchy',
      HRBP_CHANGED: 'HRBP changed',
      OTHER: 'Other',
      P1_SUPERVISOR_CHANGED: 'P+1 supervisor changed',
      P2_SUPERVISOR_CHANGED: 'P+2 supervisor changed',
      SYSTEM_USER_INVALID_ENABLED_VALUE: 'Invalid enabled value',
    },
    columns: {
      actions: 'Actions',
      column: 'Column',
      count: 'Count',
      errorName: 'Error name',
      errorType: 'Error type',
      level: 'Level',
      message: 'Message',
      row: 'Row',
      severity: 'Level',
    },
    criticalErrorMessage:
      'The file contains structural errors (e.g. missing columns) and cannot be processed. Please fix the file and re-upload.',
    criticalErrorsFound: 'Critical errors found',
    criticalErrorTitle: 'Critical Validation Error',
    detailsTitle: 'Error details',
    goToPage: 'Go to page',
    noErrors: 'No errors',
    replaceFileDialog: {
      acceptedFormats: '(accepted formats: .xls, .xlsx)',
      description:
        'Upload a new file to replace the current one. Validation will restart from the beginning.',
      dropzoneLabel: "Drag 'n' drop a file here, or click to select a file",
      title: 'Replace File',
      uploadButton: 'Upload',
    },
    status: {
      resolved: 'Resolved',
    },
    subtitle: 'Review and resolve the errors detected during file validation',
    summaryTitle: 'Errors summary by type',
    title: 'Validation Errors',
    uploadInfo: {
      fileName: 'File name',
      fileType: 'File type',
      title: 'File Information',
      uploadDate: 'Upload date',
    },
    validationInfoMessage:
      'The file contains validation errors. You can resolve errors in the system or fix the file and re-upload.',
    validationInfoTitle: 'Validation Information',
  },
};
